import {
  IonIcon,
  IonItem,
  IonLabel,
  IonListHeader,
  IonRouterContext,
} from '@ionic/react';
import {
  cloudDownloadOutline,
  copyOutline,
  createOutline,
  documentLockOutline,
  trashBin,
} from 'ionicons/icons';
import { ReactElement, useContext } from 'react';
import { useHasPermission } from '../../../api/authorization.provider';
import { PermissionType, Scratchpad } from '../../../api/graphql/generated';
import { useCopyScratchpadModal } from '../../../api/hooks/copy-scratchpad.hooks';
import { useHasFeature } from '../../../api/hooks/feature.hooks';
import { useScratchpadUtils } from '../../../api/hooks/scratchpad.hooks';
import i18n from '../../../i18n';
import {
  useIsMachineActive,
  useMachineContext,
} from '../../../pages/machine/machine-context-provider';
import { useBeaconRouter } from '../../../route/route.hooks';
import { WithConfirmation } from '../../button/with-confirmation';
import { addToEndOfPlaylist } from '../../icons/add-to-end-of-playlist';
import { createNewPlaylist } from '../../icons/create-new-playlist';
import { isProgramStopped } from '../machine.utils';
import { ScratchpadAlreadyInPlaylist } from '../playlist/scratchpad-already-in-playlist';
import { useScratchpadPlaylistHelpers } from '../playlist/scratchpad-playlist.hooks';

interface ScratchpadItemDetailProps {
  onClose: (cb: () => void) => void;
  scratchpad?: Partial<Scratchpad>;
  teamId: string;
}

const ScratchpadItemDetail = (
  props: ScratchpadItemDetailProps
): ReactElement => {
  const { onClose, scratchpad, teamId } = props;
  const isMachineActive = useIsMachineActive();

  const { push } = useContext(IonRouterContext);
  const { addScratchpadToActivatedPlaylist, createNewPlaylistWithScratchpad } =
    useScratchpadPlaylistHelpers({
      machineId: scratchpad?.machineId,
    });

  const { remove, restore } = useScratchpadUtils();

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const canDeleteScratchpad = useHasPermission(
    'delete',
    'scratchpad',
    scratchpad
  );

  const canCreateScratchpad = useHasPermission(
    'create',
    PermissionType.SCRATCHPAD,
    {
      machineId: scratchpad?.machineId,
    }
  );

  const isPartActive = scratchpad?.active ?? false;
  const canUpdate = canUpdateScratchpad && isMachineActive;
  const canDelete = canDeleteScratchpad && isMachineActive;

  // Feature toggle for scratchpad copying
  const hasScratchpadCopyFeature = useHasFeature<boolean>(
    'beacon-scratchpad-copy'
  );
  const canCopy =
    hasScratchpadCopyFeature &&
    canCreateScratchpad &&
    isMachineActive &&
    scratchpad?.id !== undefined;

  const { builder } = useBeaconRouter();

  // Copy scratchpad functionality
  const { openModal, CopyModal } = useCopyScratchpadModal();

  const handleCopySuccess = () => {
    // The list will refresh automatically due to refetchQueries in the hook
    onClose(() => {});
  };

  return (
    <>
      <IonListHeader>
        <IonLabel>{scratchpad?.name}</IonLabel>
      </IonListHeader>
      {scratchpad?.id && scratchpad?.machineId && (
        <ScratchpadAlreadyInPlaylist scratchpadId={scratchpad.id} />
      )}
      <IonItem
        className={'transparent-background'}
        lines="none"
        href="#"
        detail={false}
        onClick={(e) => {
          e.preventDefault();
          onClose(() => {
            if (scratchpad && scratchpad.machineId && scratchpad.id) {
              push(
                builder.getMachineScratchpadRoute(
                  teamId,
                  scratchpad.machineId,
                  scratchpad.id
                )
              );
            }
          });
        }}
      >
        <IonIcon
          icon={canUpdate ? createOutline : documentLockOutline}
          slot="start"
        />
        <IonLabel>
          {canUpdate
            ? i18n.t('common:scratchpad_edit_this_part')
            : i18n.t('common:scratchpad_view_this_part')}
        </IonLabel>
      </IonItem>
      {canCopy && (
        <IonItem
          className={'transparent-background'}
          lines="none"
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            openModal();
          }}
        >
          <IonIcon icon={copyOutline} slot="start" />
          <IonLabel>{i18n.t('common:scratchpad_copy_part')}</IonLabel>
        </IonItem>
      )}

      <AddScratchpadToPlaylist
        scratchpad={scratchpad}
        onSelect={() => {
          onClose(() => {
            if (scratchpad?.id) {
              addScratchpadToActivatedPlaylist(scratchpad?.id);
            }
          });
        }}
      />
      <CreateNewPlaylistWithScratchpad
        scratchpad={scratchpad}
        onSelect={() => {
          onClose(() => {
            if (scratchpad) {
              createNewPlaylistWithScratchpad(scratchpad);
            }
          });
        }}
      />

      {canDelete && isPartActive && (
        <WithConfirmation
          header={i18n.t('common:scratchpad_deactivate_confirmation')}
        >
          {({ onConfirmed }) => (
            <IonItem
              className={'transparent-background'}
              lines="none"
              href="#"
              detail={false}
              onClick={(e) => {
                e.preventDefault();
                onConfirmed({
                  onConfirmation: () => {
                    onClose(() => {
                      if (scratchpad?.id) {
                        remove(scratchpad.id);
                      }
                    });
                  },
                });
              }}
            >
              <IonIcon icon={trashBin} slot="start" color="danger" />
              <IonLabel color="danger">
                {i18n.t('common:scratchpad_deactivate_part')}
              </IonLabel>
            </IonItem>
          )}
        </WithConfirmation>
      )}
      {canDelete && !isPartActive && (
        <IonItem
          className={'transparent-background'}
          lines="none"
          href="#"
          detail={false}
          onClick={(e) => {
            e.preventDefault();
            onClose(() => {
              if (scratchpad?.id) {
                restore(scratchpad.id);
              }
            });
          }}
        >
          <IonIcon icon={cloudDownloadOutline} slot="start" color="success" />
          <IonLabel color="success">
            {i18n.t('common:scratchpad_activate_part')}
          </IonLabel>
        </IonItem>
      )}
      {scratchpad && (
        <CopyModal scratchpad={scratchpad} onSuccess={handleCopySuccess} />
      )}
    </>
  );
};

interface PlaylistOptionProps {
  scratchpad?: Partial<Scratchpad>;
  onSelect: () => void;
}

const AddScratchpadToPlaylist = (
  props: PlaylistOptionProps
): ReactElement | null => {
  const { onSelect, scratchpad } = props;
  const { machine } = useMachineContext() ?? {};
  const programStopped = isProgramStopped(
    machine?.state?.transient?.status?.programState
  );
  const isMachineActive = useIsMachineActive();

  const scratchpadPlaylistPermissionObject = {
    machineId: scratchpad?.machineId,
  };

  const canUpdateScratchpadPlaylist = useHasPermission(
    'update',
    'scratchpad_playlist',
    scratchpadPlaylistPermissionObject
  );
  const canUpdate = canUpdateScratchpadPlaylist && isMachineActive;

  if (canUpdate) {
    return (
      <IonItem
        button={true}
        className={'transparent-background'}
        lines="none"
        href="#"
        detail={false}
        onClick={(e) => {
          e.preventDefault();
          onSelect();
        }}
        disabled={!programStopped || !scratchpad?.active}
      >
        <IonIcon icon={addToEndOfPlaylist} slot="start" />
        <IonLabel>{i18n.t('common:scratchpad_playlist_add_part')}</IonLabel>
      </IonItem>
    );
  } else {
    return null;
  }
};

const CreateNewPlaylistWithScratchpad = (
  props: PlaylistOptionProps
): ReactElement | null => {
  const { onSelect, scratchpad } = props;
  const { machine } = useMachineContext() ?? {};
  const programStopped = isProgramStopped(
    machine?.state?.transient?.status?.programState
  );
  const isMachineActive = useIsMachineActive();

  const scratchpadPlaylistPermissionObject = {
    machineId: scratchpad?.machineId,
  };

  const canCreateScratchpadPlaylist = useHasPermission(
    'create',
    'scratchpad_playlist',
    scratchpadPlaylistPermissionObject
  );
  const canCreate = canCreateScratchpadPlaylist && isMachineActive;

  if (canCreate) {
    return (
      <IonItem
        className={'transparent-background'}
        lines="none"
        href="#"
        detail={false}
        button={true}
        onClick={(e) => {
          e.preventDefault();
          onSelect();
        }}
        disabled={!programStopped || !scratchpad?.active}
      >
        <IonIcon icon={createNewPlaylist} slot="start" />
        <IonLabel>
          {i18n.t('common:scratchpad_playlist_create_new_with_part')}
        </IonLabel>
      </IonItem>
    );
  } else {
    return null;
  }
};

export {
  ScratchpadItemDetail,
  AddScratchpadToPlaylist,
  CreateNewPlaylistWithScratchpad,
};
